import { createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';

// Selectors
import { selectGroups } from '../../services/groups/selectors';
import { selectDataGroupedByEntityId } from '../../services/groupEntities/selectors';
import { selectCurrentEntityId } from '../../services/currentEntityId/selectors';
import { makeSelectQueryStringNumberValue } from '../../workspaces/react-router-selectors';

// Utils
import orderBy from '../../utils/custom-orderBy';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);
const selectResetOnNewGroupCreation = (state, props) =>
    props.resetOnNewGroupCreation;

const selectCurrentEntityGroupsData = createDeepEqualSelector(
    [
        selectDataGroupedByEntityId,
        selectCurrentEntityId,
        makeSelectQueryStringNumberValue('selectedCurrentEntityId'),
        makeSelectQueryStringNumberValue('newGroup'),
        selectResetOnNewGroupCreation
    ],
    (
        dataGroupedByEntityIdState,
        globalCurrentEntityId,
        selectedCurrentEntityId,
        newGroupId,
        resetOnNewGroupCreation
    ) => {
        if (
            !globalCurrentEntityId &&
            (!selectedCurrentEntityId && selectedCurrentEntityId !== 0)
        ) {
            return undefined;
        }

        // Render full list when a new group is created from within the form.
        if (resetOnNewGroupCreation && newGroupId) return undefined;

        if (selectedCurrentEntityId === 0) return undefined;

        if (selectedCurrentEntityId) {
            return dataGroupedByEntityIdState[selectedCurrentEntityId] || [];
        }

        return dataGroupedByEntityIdState[globalCurrentEntityId] || [];
    }
);

const selectCurrentEntityGroupIds = createDeepEqualSelector(
    selectCurrentEntityGroupsData,
    entityDataState =>
        entityDataState && entityDataState.map(value => value.groupId)
);

const selectCurrentEntityGroups = createDeepEqualSelector(
    [selectCurrentEntityGroupIds, selectGroups],
    (currentEntityGroupIds, groupsState) => {
        if (!currentEntityGroupIds) return groupsState;
        return currentEntityGroupIds
            .map(value => groupsState[value])
            .filter(value => value);
    }
);

const selectData = createDeepEqualSelector(
    selectCurrentEntityGroups,
    currentEntityGroupsState => Object.values(currentEntityGroupsState)
);

const selectFilteredData = createDeepEqualSelector(
    selectData,
    dataState => dataState
);

const selectOrderedData = createDeepEqualSelector(
    selectFilteredData,
    filteredDataState => orderBy(filteredDataState, 'name')
);

const selectOptions = createDeepEqualSelector(
    selectOrderedData,
    orderedDataState =>
        orderedDataState.map(({ id, name }) => ({
            key: id,
            text: name,
            value: id
        }))
);

export { selectOptions };
